import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone } from 'lucide-react';

/* Shared building blocks for the Saudi Visa Framework 2026 hub-and-spoke cluster. */

export const HUB_PATH = '/saudi-visa-framework-2026/';

export const Crumbs = ({ current, mid }) => (
  <nav aria-label="Breadcrumb" className="mb-6" data-testid="framework-breadcrumb">
    <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-slate-300">
      <li><Link to="/" className="hover:text-[#38CAFC] transition-colors">Home</Link></li>
      <li aria-hidden="true"><ChevronRight className="w-3.5 h-3.5 text-slate-500" /></li>
      {mid && (
        <>
          <li><Link to={HUB_PATH} className="hover:text-[#38CAFC] transition-colors">{mid}</Link></li>
          <li aria-hidden="true"><ChevronRight className="w-3.5 h-3.5 text-slate-500" /></li>
        </>
      )}
      <li aria-current="page" className="text-[#38CAFC] font-medium">{current}</li>
    </ol>
  </nav>
);

export const useBreadcrumbSchema = (items) => {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: it.name,
        item: `https://zafco.in${it.path}`,
      })),
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-framework-breadcrumb', '1');
    // Escape `<` so content can never terminate the script block early.
    el.text = JSON.stringify(schema).replace(/</g, '\\u003c');
    document.head.appendChild(el);
    return () => {
      document.head.querySelectorAll('script[data-framework-breadcrumb]').forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const TrustBox = ({ author, reviewer, standard, standardLabel = 'Regulatory Standard' }) => (
  <div className="bg-[#F8F9FA] border-l-4 border-[#000080] rounded-r-lg p-5 mb-8 space-y-1.5" data-testid="author-trust-box">
    <p className="text-sm text-slate-700"><strong className="text-[#000080]">Authored By:</strong> {author}</p>
    <p className="text-sm text-slate-700"><strong className="text-[#000080]">Technical Reviewer:</strong> {reviewer}</p>
    <p className="text-sm text-slate-700"><strong className="text-[#000080]">{standardLabel}:</strong> {standard}</p>
  </div>
);

export const InfoCallout = ({ children }) => (
  <div className="bg-[#E7F3FE] border-l-4 border-[#2196F3] rounded-r-lg p-5 my-6 text-sm text-slate-800 leading-relaxed">
    {children}
  </div>
);

export const WarnCallout = ({ children }) => (
  <div className="bg-[#FFF3CD] border-l-4 border-[#FFC107] rounded-r-lg p-5 my-6 text-sm text-slate-800 leading-relaxed">
    {children}
  </div>
);

export const DataTable = ({ headers, rows, testId }) => (
  <div className="overflow-x-auto my-6 rounded-xl border border-slate-200 shadow-sm" data-testid={testId}>
    <table className="w-full text-sm text-left border-collapse">
      <thead>
        <tr className="bg-slate-100">
          {headers.map((h) => (
            <th key={h} className="px-4 py-3 font-bold text-[#000080] border-b border-slate-200 whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 ? 'bg-slate-50/60' : 'bg-white'}>
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 border-b border-slate-100 text-slate-700 align-top leading-relaxed">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const FrameworkCTA = () => (
  <div className="bg-[#0A192F] text-white p-7 sm:p-8 rounded-2xl my-10 shadow-lg" data-testid="framework-cta">
    <h3 className="text-lg sm:text-xl font-bold text-[#38CAFC] font-syne mb-2">Need Enterprise Mobility or Visa Stamping Assistance?</h3>
    <p className="text-sm text-slate-300 leading-relaxed mb-5">Zafco provides MEA-licensed end-to-end recruitment, QVP attestation, and consular deployment for Saudi Arabia.</p>
    <div className="flex flex-wrap gap-3">
      <Link
        to="/contact?source=saudi-framework-2026"
        className="inline-flex items-center gap-2 bg-[#38CAFC] hover:bg-[#5ed6ff] text-[#0A192F] font-bold text-sm px-6 py-3 rounded-md transition-colors"
        data-testid="framework-cta-consult"
      >
        Consult Our Saudi Specialists
      </Link>
      <a
        href="tel:+919958848998"
        className="inline-flex items-center gap-2 border border-white/60 hover:bg-white/10 text-white font-semibold text-sm px-6 py-3 rounded-md transition-colors"
        data-testid="framework-cta-call"
      >
        <Phone className="w-4 h-4" aria-hidden="true" /> Call +91-9958848998
      </a>
    </div>
  </div>
);

export const PageHero = ({ crumb, mid, title }) => (
  <section className="bg-[#0A192F] text-white">
    <div className="container mx-auto px-6 max-w-5xl py-12 lg:py-16">
      <Crumbs current={crumb} mid={mid} />
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne leading-tight text-white">{title}</h1>
    </div>
  </section>
);

export const SectionH2 = ({ id, children }) => (
  <h2 id={id} className="text-2xl sm:text-3xl font-bold text-[#000080] font-syne mt-12 mb-5 leading-tight">{children}</h2>
);

export const ILink = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="text-[#000080] font-semibold underline decoration-2 underline-offset-2 hover:text-[#0056B3] transition-colors">{children}</Link>
);
